import type { TOC } from '@ember/component/template-only';
import type { ComponentLike, WithBoundArgs } from '@glint/template';
import { hash } from '@ember/helper';
import { or } from '../utilities.ts';
// TODO: this could potentially benefit from tailwind variants for finer
// grained size/spacing (small or xs variants for more data-dense views,
// for example) or control over preDescined striping and divider behaviors
// https://www.tailwind-variants.org/docs/variants

interface DescriptionGroupSignature {
  Args: {
    striped?: boolean;
    compact?: boolean;
    cols?: 2 | 3 | 4 | 5 | 6; // default 3
  };
  Blocks: { default: [] };
  Element: HTMLElement;
}

const gridColsClass = (cols: number = 3) =>
  (
    ({
      2: 'sm:grid-cols-2',
      3: 'sm:grid-cols-3',
      4: 'sm:grid-cols-4',
      5: 'sm:grid-cols-5',
      6: 'sm:grid-cols-6',
    }) as const
  )[cols] ?? 'sm:grid-cols-3';

const detailsSpanClass = (cols: number = 3) =>
  (
    ({
      2: 'sm:col-span-1',
      3: 'sm:col-span-2',
      4: 'sm:col-span-3',
      5: 'sm:col-span-4',
      6: 'sm:col-span-5',
    }) as const
  )[cols] ?? 'sm:col-span-2';

const descriptionGroupClasses = (opts?: {
  striped?: boolean;
  compact?: boolean;
  cols?: number;
}) => {
  const { striped, compact, cols = 3 } = opts || {};
  return [
    compact ? 'p-1' : 'px-4 py-3 sm:px-3',
    'sm:grid',
    gridColsClass(cols),
    'sm:gap-4',
    striped && 'even:bg-gray-50',
  ]
    .filter(Boolean)
    .join(' ');
};

const _DescriptionGroup: TOC<DescriptionGroupSignature> = <template>
  <div
    class={{descriptionGroupClasses (hash striped=@striped compact=@compact cols=@cols)}}
    ...attributes
  >
    {{yield}}
  </div>
</template>;

const DescriptionTerm: TOC<{
  Args: { cols?: 2 | 3 | 4 | 5 | 6 };
  Element: HTMLElement;
  Blocks: { default: [] };
}> = <template>
  <dt class="tio-text-sm-medium leading-6 min-w-0" ...attributes>
    {{yield}}
  </dt>
</template>;

const DescriptionDetails: TOC<{
  Args: { cols?: 2 | 3 | 4 | 5 | 6 };
  Element: HTMLElement;
  Blocks: { default: [] };
}> = <template>
  <dd
    class="{{detailsSpanClass (or @cols 3)}}
      mt-1 text-sm leading-6 text-gray-700 dark:text-white sm:mt-0"
    ...attributes
  >
    {{yield}}
  </dd>
</template>;

interface DescriptionListSignature {
  Args: {
    striped?: boolean;
    compact?: boolean;
    divided?: boolean;
    cols?: 2 | 3 | 4 | 5 | 6; // default 3
  };
  Blocks: {
    default: [
      {
        Group: WithBoundArgs<typeof _DescriptionGroup, 'striped' | 'compact' | 'cols'>;
        Term: ComponentLike<{
          Args: { cols?: 2 | 3 | 4 | 5 | 6 };
          Element: HTMLElement;
          Blocks: { default: [] };
        }>;
        Details: ComponentLike<{
          Args: { cols?: 2 | 3 | 4 | 5 | 6 };
          Element: HTMLElement;
          Blocks: { default: [] };
        }>;
      },
    ];
  };
  Element: HTMLDListElement;
}

const descriptionListClasses = (opts?: { divided?: boolean }) =>
  [opts?.divided && 'divide-y divide-gray-100'].filter(Boolean).join(' ');

const DescriptionList: TOC<DescriptionListSignature> = <template>
  <dl ...attributes class={{descriptionListClasses (hash divided=@divided)}}>
    {{yield
      (hash
        Group=(component _DescriptionGroup striped=@striped compact=@compact cols=@cols)
        Term=(component DescriptionTerm cols=@cols)
        Details=(component DescriptionDetails cols=@cols)
      )
    }}
  </dl>
</template>;

export default DescriptionList;
